import { MOBILE_BREAKPOINT_NAME } from 'layout-data-lib/GridLayoutDataTree/constants';

/**
 * Calculates the offset of a pegboard item from the top-left corner of the pegboard.
 * @param cellSize - The size of a cell.
 * @param gap - The gap between cells.
 * @param targetIndex - The index of the target item.
 * @param scaleFactor - The scale factor of the item.
 * @returns The offset of the target item.
 */
export function getPegboardItemOffset(cellSize, gap, targetIndex, scaleFactor = 1) {
  // Since targetIndex on a pegboard is 1-based, we need to subtract by 1 since
  // the offset is the space between the item and the start of the pegboard.
  const index = targetIndex - 1;
  const gapOffset = index * gap * scaleFactor;
  const cellOffset = index * cellSize;
  return cellOffset + gapOffset;
}

/**
 * Calculates a dimension of a pegboard item along a single track.
 * @param cellSize - The size of a cell.
 * @param gap - The gap between cells.
 * @param spanned - The number of cells spanned by the item.
 * @param scaleFactor - The scale factor of the item.
 * @returns The dimension of the item.
 */
export function getPegboardItemDimension(cellSize, gap, spanned, scaleFactor = 1) {
  // Since gap is between items, we need to subtract spanned by 1. We also need
  // to apply the scale factor since we get gap from the layout which doesn't
  // factor in scale.
  const totalGap = gap * (spanned - 1) * scaleFactor;
  const totalSize = spanned * cellSize;
  return totalSize + totalGap;
}

/**
 * Rounds the given value to the nearest 100th decimal place
 * @param value number to round
 * @returns value rounded to nearest 100th decimal
 */
function roundToNearestHundredthDecimal(value) {
  return parseFloat(value.toFixed(2));
}

/**
 * Calculates the number of rows or columns an element on a pegboard should span.
 * @param elementSize - The height or width of the element.
 * @param cellSize - The size of a row or column in the pegboard.
 * @param gap - The size of the gap between cells.
 * @returns The number of rows or columns the element should span on the pegboard.
 */
export function getPegboardElementComputedSpanning({
  elementSize,
  cellSize,
  gap,
  maxSpan
}) {
  if (elementSize <= cellSize) return 1;
  const elementSizeAfterFirstCell = elementSize - cellSize;
  const roundedCellsSpanned = roundToNearestHundredthDecimal(elementSizeAfterFirstCell / (cellSize + gap));
  const computedSpanning = 1 + Math.ceil(roundedCellsSpanned);
  if (maxSpan) {
    return Math.min(computedSpanning, maxSpan);
  }
  return computedSpanning;
}

/**
 * Converts a pixel delta to a row delta based on row size and gap.
 * @param pixelDelta - The pixel distance to convert.
 * @param rowSize - The size of a single row.
 * @param rowGap - The gap between rows.
 * @returns The number of rows corresponding to the pixel delta, rounded to the nearest integer.
 */
export function calculatePegboardRowDelta({
  pixelDelta,
  rowSize,
  rowGap
}) {
  const rowStep = rowSize + rowGap;
  if (rowStep === 0) return 0;
  return Math.round(pixelDelta / rowStep);
}

/**
 * Calculates a new row count clamped to a minimum value.
 * @param originalRowCount - The current number of rows.
 * @param deltaRows - The number of rows to add or remove.
 * @param minRowCount - The minimum allowed row count.
 * @returns The new row count, no less than minRowCount.
 */
export function calculateClampedRowCount({
  originalRowCount,
  deltaRows,
  minRowCount
}) {
  return Math.max(minRowCount, originalRowCount + deltaRows);
}

/**
 * Calculates the clamped position for pasting a pegboard item.
 * Applies an offset to a base position while ensuring the item stays within grid bounds.
 * @param rowStart - The starting row to offset from.
 * @param columnStart - The starting column to offset from.
 * @param offset - The offset to apply to the base position.
 * @param pegboardRows - The total number of rows in the pegboard.
 * @param pegboardColumns - The total number of columns in the pegboard.
 * @param rowsSpanned - The number of rows spanned by the item.
 * @param columnsSpanned - The number of columns spanned by the item.
 * @returns The clamped row and column start position.
 */
export function getPegboardPastePosition({
  rowStart,
  columnStart,
  offset,
  pegboardRows,
  pegboardColumns,
  rowsSpanned,
  columnsSpanned
}) {
  const maxValidRow = Math.max(1, pegboardRows - rowsSpanned + 1);
  const maxValidCol = Math.max(1, pegboardColumns - columnsSpanned + 1);
  return {
    rowStart: Math.max(1, Math.min(rowStart + offset, maxValidRow)),
    columnStart: Math.max(1, Math.min(columnStart + offset, maxValidCol))
  };
}
export const PEGBOARD_ROW_MULTIPLIER = 0.02;
export const MOBILE_BREAKPOINT_ROW_SIZE = 32;

/**
 * Calculates the size of a row in a pegboard.
 * @param activeBreakpoint - The active breakpoint.
 * @param viewportWidth - The width of the viewport.
 * @param scale - The scale of the item.
 * @returns The size of the row.
 */
export const getPegboardRowSize = (activeBreakpoint, viewportWidth, scale = 1) => {
  if (activeBreakpoint === MOBILE_BREAKPOINT_NAME) {
    return scale * MOBILE_BREAKPOINT_ROW_SIZE;
  }
  return scale * viewportWidth * PEGBOARD_ROW_MULTIPLIER;
};